import { z } from 'zod';
import { baseEntitySchema, idSchema, instantSchema } from './common.js';
import { stageCountSchema } from './boards.js';
import { customValuesSchema } from './fields.js';
import { filterSchema, sortSchema } from './filters.js';
import { externalIdsSchema, pipelinePlaceSchema, primaryContactSchema, stageRefSchema, } from './projects.js';
/**
 * Jobs (RELAY-950): the recruiting side of a project — a role to fill under a deal. The job
 * moves through the Job Status pipeline (RELAY-948) by stage id, and its people are user and
 * contact REFERENCES: recruiter, coordinator and account manager are CRM users, the hiring
 * manager is a client contact. In Relay those four are free text (`crm_coordinator`,
 * `crm_account_manager`, `crm_hiring_manager`…), which is what made reassignment and "my jobs"
 * impossible to do reliably there.
 *
 * Column names follow `recruiting.job_descriptions` wherever the concept is the same
 * (`title`, `content`, `location`, `job_type`, `salary_*`, `open_date`, `closed_date`,
 * `interview_date_time`, `onboarding_date_time`, `va_presentation_date_time`,
 * `status_changed_at`, `recruit_crm_id`, `recruit_crm_slug`), so the move from Relay is a mapped
 * `INSERT … SELECT`. The full mapping is in docs/data-dictionary/jobs.md.
 *
 * (`job.ts` would collide with the BullMQ job contracts in `jobs.ts`, hence the file name.)
 */
export const SALARY_PERIODS = ['hourly', 'monthly', 'yearly'];
const text = (max) => z.string().trim().min(1).max(max);
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
const instant = instantSchema.nullable().optional();
const count = z.number().int().min(0).max(100_000);
const salary = z.number().int().min(0).max(100_000_000).nullable().optional();
const jobFields = {
    title: text(300),
    /** The job description text; `content` as in recruiting.job_descriptions. */
    content: z.string().max(200_000).nullable().optional(),
    /** A Job Status; the pipeline's first open stage when omitted. */
    stageId: idSchema.optional(),
    openings: count,
    filled: count,
    jobType: optionalText(60),
    location: optionalText(200),
    salaryMin: salary,
    salaryMax: salary,
    salaryCurrency: z
        .string()
        .trim()
        .regex(/^[A-Za-z]{3}$/, 'a three-letter currency code')
        .transform((c) => c.toUpperCase())
        .nullable()
        .optional(),
    /**
     * Relay's own spelling (`yearly`); its RecruitCRM-shaped values map onto these — "Annual
     * Salary" → yearly, "Monthly Salary" → monthly, "Hourly Salary" → hourly (data dictionary).
     */
    salaryPeriod: z.enum(SALARY_PERIODS).nullable().optional(),
    recruiterId: idSchema.nullable().optional(),
    coordinatorId: idSchema.nullable().optional(),
    /** Defaults to the creator on create. */
    accountManagerId: idSchema.nullable().optional(),
    /** A contact (the client's hiring manager), not a user. */
    hiringManagerContactId: idSchema.nullable().optional(),
    /** A bundle of the job's own client (any deal of its project); null takes the job out. */
    bundleId: idSchema.nullable().optional(),
    interviewDateTime: instant,
    onboardingDateTime: instant,
    vaPresentationDateTime: instant,
    /** Defaults to now on create. */
    openDate: instant,
    closedDate: instant,
    recruitCrmId: optionalText(60),
    recruitCrmSlug: optionalText(200),
    externalIds: externalIdsSchema.optional(),
    /** Custom field values by definition key (TS-1840). No default: a PATCH touches only what it carries. */
    custom: customValuesSchema.optional(),
};
/** Salary bounds in order, and no more filled than there are openings. */
function jobRules(j, ctx) {
    if (j.salaryMin != null && j.salaryMax != null && j.salaryMin > j.salaryMax)
        ctx.addIssue({ code: 'custom', path: ['salaryMax'], message: 'below salaryMin' });
    if (j.openings !== undefined && j.filled !== undefined && j.filled > j.openings)
        ctx.addIssue({ code: 'custom', path: ['filled'], message: 'more filled than openings' });
}
export const jobInputSchema = z
    .object({
    ...jobFields,
    /** The deal the job sits under; the project is taken from it. */
    dealId: idSchema,
    /**
     * The Job Flow pipeline the job starts in (`stageId`, when given, must be one of its stages;
     * else its first open stage). An EXTRA pipeline puts the job there too: its status, which is
     * always the default pipeline's, starts at that pipeline's first open stage. Omitted: the default.
     */
    pipelineId: idSchema.optional(),
    openings: count.default(1),
    filled: count.default(0),
})
    .superRefine(jobRules);
/** Never `dealId`/`projectId`: a job belongs to its deal for life. No defaults (see deals). */
export const jobPatchSchema = z
    .object(jobFields)
    .partial()
    .refine((p) => Object.keys(p).length > 0, { message: 'nothing to change' })
    .superRefine(jobRules);
/** The whole job after a patch is applied must still pass the rules. */
export const jobStateRules = jobRules;
export const jobSchema = baseEntitySchema.extend({
    dealId: idSchema,
    /** The bundle of its deal the job fills, or null: a standalone job. */
    bundleId: idSchema.nullable(),
    /** The deal's project, kept on the job so lists filter by project without a join. */
    projectId: idSchema,
    /** The project's name and company, so a list across projects can say where a job belongs. */
    project: z.object({ id: idSchema, name: z.string(), companyName: z.string() }),
    primaryContact: primaryContactSchema.nullable().optional(),
    title: z.string(),
    content: z.string().nullable(),
    /** The Job Status in the DEFAULT Job Flow pipeline, where every job always has a place. */
    stageId: idSchema,
    stage: stageRefSchema,
    /** Every live Job Flow pipeline the job is in, the default first, then in the order it joined. */
    pipelines: z.array(pipelinePlaceSchema),
    openings: z.number().int(),
    filled: z.number().int(),
    jobType: z.string().nullable(),
    location: z.string().nullable(),
    salaryMin: z.number().int().nullable(),
    salaryMax: z.number().int().nullable(),
    salaryCurrency: z.string().nullable(),
    salaryPeriod: z.enum(SALARY_PERIODS).nullable(),
    recruiterId: idSchema.nullable(),
    coordinatorId: idSchema.nullable(),
    accountManagerId: idSchema.nullable(),
    hiringManagerContactId: idSchema.nullable(),
    interviewDateTime: instantSchema.nullable(),
    onboardingDateTime: instantSchema.nullable(),
    vaPresentationDateTime: instantSchema.nullable(),
    openDate: instantSchema.nullable(),
    closedDate: instantSchema.nullable(),
    /** When the job last changed Job Status. */
    statusChangedAt: instantSchema.nullable(),
    recruitCrmId: z.string().nullable(),
    recruitCrmSlug: z.string().nullable(),
    externalIds: externalIdsSchema,
    /** Custom field values by definition key (TS-1840); only the fields the caller's role may see. */
    custom: customValuesSchema.default({}),
});
/**
 * `POST /jobs/{id}:move-status`. Reaching a terminal Job Status (Filled, Closed) stamps
 * `closedDate` with now unless given; reopening clears it.
 */
export const jobMoveSchema = z.object({
    stageId: idSchema,
    closedDate: instantSchema.optional(),
});
/** The job fields a filter may name, with the type that decides operators and values. */
export const JOB_FIELDS = {
    id: 'reference',
    dealId: 'reference',
    bundleId: 'reference',
    projectId: 'reference',
    title: 'text',
    content: 'text',
    stageId: 'reference',
    openings: 'number',
    filled: 'number',
    jobType: 'single_select',
    location: 'text',
    salaryMin: 'number',
    salaryMax: 'number',
    salaryCurrency: 'single_select',
    salaryPeriod: 'single_select',
    recruiterId: 'user',
    coordinatorId: 'user',
    accountManagerId: 'user',
    hiringManagerContactId: 'reference',
    interviewDateTime: 'datetime',
    onboardingDateTime: 'datetime',
    vaPresentationDateTime: 'datetime',
    openDate: 'datetime',
    closedDate: 'datetime',
    statusChangedAt: 'datetime',
    recruitCrmId: 'text',
    createdAt: 'datetime',
    updatedAt: 'datetime',
};
/**
 * What the job lists (`GET /jobs`, `POST /jobs:search|counts`) filter on: `JOB_FIELDS` plus the
 * job's project's people — `responsibleId` (someone in ANY function of the project) and
 * `involvedId` (the job's recruiter, coordinator or account manager OR a responsible of its
 * project; the Jobs list's "Mine"). Not columns, so they stay out of `JOB_FIELDS`, which trigger
 * conditions read.
 */
export const JOB_SEARCH_FIELDS = {
    ...JOB_FIELDS,
    responsibleId: 'user',
    involvedId: 'user',
};
export const JOB_SORTABLE = [
    'createdAt',
    'updatedAt',
    'title',
    'openings',
    'openDate',
    'closedDate',
    'interviewDateTime',
    'onboardingDateTime',
    'vaPresentationDateTime',
    'statusChangedAt',
];
/**
 * `POST /jobs:search` — the same keys `GET /jobs` takes as query parameters. `pipelineId` lists only
 * the jobs in that Job Flow pipeline (an archived place is left out), and `stageId` then means the
 * status there; without it every job is listed, with its status in the default pipeline.
 */
export const jobSearchSchema = z.object({
    q: z.string().trim().min(1).max(200).optional(),
    projectId: idSchema.optional(),
    dealId: idSchema.optional(),
    pipelineId: idSchema.optional(),
    filter: filterSchema.optional(),
    sort: z.array(sortSchema).max(1).optional(),
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(200).default(50),
});
/**
 * `POST /jobs:counts` (RELAY-953): how many jobs each Job Status holds for the same filters a
 * search takes, grouped by the server. The board shows a column's total without loading it.
 */
export const jobCountsRequestSchema = jobSearchSchema.pick({
    q: true,
    projectId: true,
    dealId: true,
    pipelineId: true,
    filter: true,
});
/** The same shape every board's counts return (`boards.ts`). */
export const jobStageCountSchema = stageCountSchema;
//# sourceMappingURL=jobs-records.js.map